import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import AuthService from "../api/authService";
import { SESSION_EXPIRED_EVENT } from "../api/axios";

const AuthContext = createContext();

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

// Restore a stored session, but only if its token has not already expired.
const restoreSession = () => {
  if (AuthService.isTokenExpired()) {
    AuthService.logout();
    return null;
  }
  return AuthService.getCurrentUser() || null;
};

export const AuthProvider = ({ children }) => {
  // Restore any existing session synchronously on first render
  const [currentUser, setCurrentUser] = useState(restoreSession);
  // Set when the session was ended by expiry rather than an explicit logout,
  // so the login page can explain why the user is back there.
  const [sessionExpired, setSessionExpired] = useState(false);

  const login = async (credentials) => {
    const response = await AuthService.login(credentials);
    setSessionExpired(false);
    setCurrentUser(response.user);
    return response;
  };

  const logout = useCallback(() => {
    AuthService.logout();
    setSessionExpired(false);
    setCurrentUser(null);
  }, []);

  const expireSession = useCallback(() => {
    AuthService.logout();
    setSessionExpired(true);
    setCurrentUser(null);
  }, []);

  // 1. React to the backend rejecting our token on any request (axios
  //    interceptor fires this event after clearing storage).
  useEffect(() => {
    window.addEventListener(SESSION_EXPIRED_EVENT, expireSession);
    return () =>
      window.removeEventListener(SESSION_EXPIRED_EVENT, expireSession);
  }, [expireSession]);

  // 2. While logged in, schedule a logout for the moment the token's `exp`
  //    claim passes, and re-check whenever the tab regains focus (timers do
  //    not fire reliably in background tabs or after the machine sleeps).
  useEffect(() => {
    if (!currentUser) return undefined;

    const expiry = AuthService.getTokenExpiry();
    if (expiry === null) return undefined;

    // Already-expired tokens are rejected in restoreSession, so `remaining`
    // is normally positive; clamp anyway so a stale token fires on next tick.
    const remaining = Math.max(expiry - Date.now(), 0);
    const timer = setTimeout(expireSession, remaining);
    const recheck = () => {
      if (AuthService.isTokenExpired()) expireSession();
    };
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", recheck);
      document.removeEventListener("visibilitychange", recheck);
    };
  }, [currentUser, expireSession]);

  // 3. On startup, confirm the stored token is still accepted by the backend
  //    (it may have been invalidated server-side even if not yet expired).
  //    A rejection is handled by the axios interceptor via the event above.
  useEffect(() => {
    if (!currentUser) return;
    AuthService.verifySession().catch(() => {});
    // Only on mount: later logins already come with a fresh token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    currentUser,
    isAuthenticated: !!currentUser,
    sessionExpired,
    login,
    logout,
    // Session state is resolved synchronously, so consumers never wait
    loading: false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
